import { Box, Button, FileButton, Image, Input, LoadingOverlay } from "@mantine/core";
import { IconId } from "@tabler/icons-react";
import React from "react";
import { MediaApi } from "../../apis";
import { Message } from "../../utils/message";
import { useField } from "../form";
import classes from "./identity-card-upload.module.css";

export type IFile = {
  uploading: boolean;
  url?: string;
  file: Blob | null;
};

type Props = {
  value?: string | undefined | null;
  error?: string;
  onChange?: (file: IFile) => void;
  disabled?: boolean;
};

type FeildProps = {
  name: string;
  label: string;
  disabled?: boolean;
  required?: boolean;
};

function ImageFieldIdentity({ onChange, error, value, disabled }: Props) {
  const [file, setFile] = React.useState<IFile>();
  const resetRef = React.useRef<() => void>(null);

  const onFileUpload = (blob: Blob | null) => {
    if (!blob) return;

    (async () => {
      try {
        const form = new FormData();
        form.append("file", blob);

        const res = await MediaApi.uploadImage(form);

        if (onChange)
          onChange({
            file: blob,
            url: res.url,
            uploading: false,
          });

        setFile({
          file: blob,
          url: res.url,
          uploading: false,
        });
      } catch (err: any) {
        Message.error(err?.message);
      }
    })();

    setFile({
      file: blob,
      uploading: true,
    });
  };

  React.useEffect(() => {
    value &&
      setFile({
        file: null,
        url: value,
        uploading: false,
      });
  }, [value]);

  return (
    <Box className={classes.multiImageUpload}>
      <FileButton disabled={disabled} resetRef={resetRef} onChange={onFileUpload} accept="image/png,image/jpeg">
        {(props) =>
          file ? (
            <Box className={classes.imageBox} {...props}>
              <Image className={classes.image} src={file.file ? URL.createObjectURL(file.file) : file.url} alt="Random unsplash image" />
              <LoadingOverlay visible={file.uploading} opacity={0.3} loaderProps={{ size: "sm" }} />
            </Box>
          ) : (
            <Button w={400} h={250} variant="light" className={[classes.uploadButton, error && classes.error].filter(Boolean).join(" ")} {...props}>
              <IconId size={28} />
            </Button>
          )
        }
      </FileButton>
    </Box>
  );
}

export function IdentityCardUpload({ name, label, disabled, required }: FeildProps) {
  const { value, error, onChange } = useField(name);

  return (
    <Input.Wrapper label={label} error={error} required={required}>
      <ImageFieldIdentity disabled={disabled} value={value} onChange={(file: IFile) => onChange(file.url)} error={error} />
    </Input.Wrapper>
  );
}
